/* eslint-disable max-len */
import { ElementRef, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { getImage } from '@shared-private/utils/pdfmake.utils';
import {
  calculateBusinessPaymentCapacity,
  calculateBusinesssRisk,
  calculateEffort,
  calculateJointEffort,
  calculatePreviousPeriodResultsValue,
  getAntiquity,
  getAssetAddress,
  getAssetRentalArea,
  getBirthdate,
  getBusinessActivitySector,
  getBusinessRepresentativeName,
  getContractType,
  getInternationalBlacklist,
  getJudicialIncidents,
  getNonPayments,
  getPosition,
  getRole,
  getTenantDni,
  getTenantEmail,
  getTenantName,
  getTenantPhone,
  getTenantReportRole,
  getTraceabilityValue
} from '@shared-private/utils/preview-report.utils';
import { format } from 'date-fns';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
// Asigna las fuentes
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(pdfMake as any).vfs = (pdfFonts as any).vfs;
import {
  Column,
  Content,
  TDocumentDefinitions,
  TableCell
} from 'pdfmake/interfaces';

import {
  AnalysisCatalogue,
  Candidature,
  CreateAnalysisDto,
  TenantCandidature
} from '../../models';

@Injectable()
export class IncofisaReportPdfService {
  canvasElement: ElementRef<HTMLCanvasElement>;
  private jointEffort: string;
  private dangerColor = '#eb445a';

  constructor(private translate: TranslateService) {}

  async getReport(
    candidature: Candidature,
    reports: CreateAnalysisDto[],
    catalogue: AnalysisCatalogue,
    canvas: ElementRef<HTMLCanvasElement>
  ): Promise<void> {
    this.canvasElement = canvas;
    const pages: Content[] = [];

    // Obtenemos las imagenes que se pintarán en el informe
    const headerImage = await this.getHeaderImage();
    const bgImage = await this.getBgImage();
    let bulletesImage: string;
    let role: string = null;
    let canvasImg: string = null;
    const radio = 70;

    // Obtenemos la imagen del cuerpo solo si no es una empresa
    if (
      (reports.length === 1 &&
        getRole(candidature, reports[0].userId) !== 'business') ||
      reports.length > 1
    ) {
      bulletesImage = await this.getBulletesImage();
    } else {
      role = 'business';
      canvasImg = this.getScoringCanvas(radio, candidature.score);
    }

    this.jointEffort = calculateJointEffort(candidature, reports);

    // Creamos el contenido que cambia dependiendo del tenant
    reports.forEach((report: CreateAnalysisDto, i: number) => {
      const role = getRole(candidature, report.userId);
      pages.push(
        this.getContent(
          candidature,
          report,
          catalogue,
          bulletesImage,
          role,
          canvasImg,
          radio
        )
      );
      if (report.aml) {
        pages.push({ text: '', pageBreak: 'before' });
        pages.push(this.getAmlTable(report, candidature));
      }
      if (i < reports.length - 1) {
        pages.push({ text: '', pageBreak: 'before' });
      }
    });

    // Se crea el pdf con toda la info
    const pdfDefinition: TDocumentDefinitions = {
      header: this.getHeader(headerImage),
      content: pages,
      footer: this.getFooter(reports, role, candidature),
      background: {
        image: bgImage,
        absolutePosition: { x: 0, y: 75 },
        alignment: 'right',
        height: 210,
        width: 85
      },
      styles: {
        sectionTitle: {
          fontSize: 14,
          bold: true,
          marginTop: 20
        },
        inputWrapper: {
          marginTop: 10
        },
        inputText: {
          fontSize: 12,
          bold: true
        },
        tenantInputText: {
          fontSize: 12,
          bold: true,
          color: '#2ba5d8'
        }
      },
      defaultStyle: {
        fontSize: 10,
        color: '#021F37'
      },
      pageMargins: [20, 80]
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const pdf = (pdfMake as any).createPdf(pdfDefinition);
    pdf.open();
  }

  async getBulletesImage(): Promise<string> {
    return await getImage('./assets/images/incofisa-report/bulletes.png');
  }

  async getHeaderImage(): Promise<string> {
    return await getImage('./assets/images/incofisa-report/header.png');
  }

  async getBgImage(): Promise<string> {
    return await getImage(
      './assets/images/incofisa-report/incofisa-imagotipo.png'
    );
  }

  getScoringCanvas(radio: number, scoring: number): string {
    const canvas: HTMLCanvasElement = this.canvasElement.nativeElement;
    const ctx: CanvasRenderingContext2D = canvas.getContext('2d');

    // Configurar el fondo del círculo
    ctx.fillStyle = '#1794ce';

    // Calcular el centro del canvas
    const centerX: number = canvas.width / 2;
    const centerY: number = canvas.height / 2;

    // Dibujar un círculo
    ctx.beginPath();
    ctx.arc(centerX, centerY, radio, 0, 2 * Math.PI, false);
    ctx.fill();

    // Configurar el estilo del texto
    ctx.fillStyle = 'white';
    ctx.font = '28px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // Texto con dos líneas
    const linea1 = 'Scoring';
    const linea2 = `${scoring}%`;

    // Ajustar el espacio vertical entre las líneas
    const espacioVertical = 30;

    // Calcular la posición para centrar el texto dentro del círculo
    const yLinea1: number = centerY - espacioVertical / 2;
    const yLinea2: number = centerY + espacioVertical / 2;

    // Agregar texto centrado en el círculo
    ctx.fillText(linea1, centerX, yLinea1);
    ctx.fillText(linea2, centerX, yLinea2);

    // Convertir el canvas a una imagen de datos
    return canvas.toDataURL();
  }

  getHeader(b64: string): Content {
    return {
      image: b64,
      height: 50,
      width: 550,
      margin: [20, 20]
    };
  }

  private getContent(
    candidature: Candidature,
    report: CreateAnalysisDto,
    catalogue: AnalysisCatalogue,
    b64: string,
    role: string,
    canvasImg: string,
    radio: number
  ): Content[] {
    const content: Content[] = [];
    if (role !== 'business') {
      content.push(
        this.getReportAndCandidatureNumberSection(candidature, report, role)
      );
    }
    content.push(
      ...this.getPersonalDataSection(
        candidature,
        report,
        role,
        canvasImg,
        radio
      ),
      ...this.getAssetDataSection(candidature, report, role),
      ...this.getViabilityDataSection(
        candidature,
        report,
        catalogue,
        b64,
        role
      ),
      ...this.getConditioningSection(report)
    );

    return content;
  }

  private getReportAndCandidatureNumberSection(
    candidature: Candidature,
    report: CreateAnalysisDto,
    role: string
  ): Content {
    if (role !== 'business') {
      return {
        columns: [
          {
            width: '30%',
            text: `N° Informe: ${report.reportId}`
          },
          {
            text: `N° Candidatura: ${candidature.id}`
          }
        ]
      };
    } else {
      return null;
    }
  }

  private getPersonalDataSection(
    candidature: Candidature,
    report: CreateAnalysisDto,
    role: string,
    canvasImg: string,
    radio: number
  ): Content[] {
    if (role === 'business') {
      return this.getPersonalDataSectionBusiness(
        candidature,
        report,
        canvasImg,
        radio
      );
    } else {
      return this.getPersonalDataSectionTenant(candidature, report);
    }
  }

  private getPersonalDataSectionTenant(
    candidature: Candidature,
    report: CreateAnalysisDto
  ): Content[] {
    const role = getTenantReportRole(candidature, report.userId);

    return [
      {
        text: `Datos personales ${role}`,
        style: 'sectionTitle'
      },
      {
        columns: [
          {
            width: '70%',
            columns: [
              [
                {
                  columns: [
                    {
                      width: '30%',
                      style: 'inputWrapper',
                      columns: [
                        [
                          {
                            text: 'DNI'
                          },
                          {
                            text: getTenantDni(candidature, report.userId),
                            style: 'inputText'
                          }
                        ]
                      ]
                    },
                    this.getBirthdateColumn(candidature, report.userId),
                    {
                      style: 'inputWrapper',
                      columns: [
                        [
                          {
                            text: 'Nombre y apellidos'
                          },
                          {
                            text: getTenantName(candidature, report.userId),
                            style: 'inputText'
                          }
                        ]
                      ]
                    }
                  ]
                },
                {
                  columns: [
                    this.getPhoneColumn(candidature, report.userId),
                    {
                      style: 'inputWrapper',
                      columns: [
                        [
                          {
                            text: 'Correo electrónico'
                          },
                          {
                            text: getTenantEmail(candidature, report.userId),
                            style: 'inputText'
                          }
                        ]
                      ]
                    }
                  ]
                }
              ]
            ]
          },
          // Columna scoring
          {
            width: '30%',
            columns: [
              {
                alignment: 'center',
                columns: [
                  [
                    {
                      text: 'Scoring',
                      bold: true,
                      fontSize: 25
                    },
                    {
                      text: `${candidature.score} %`,
                      bold: true,
                      fontSize: 25,
                      marginTop: 10
                    }
                  ]
                ]
              }
            ]
          }
        ]
      }
    ];
  }

  private getPhoneColumn(candidature: Candidature, userId: string): Column {
    const phone = getTenantPhone(candidature, userId);

    if (phone) {
      return {
        width: '30%',
        style: 'inputWrapper',
        columns: [
          [
            {
              text: 'Teléfono'
            },
            {
              text: phone,
              style: 'inputText'
            }
          ]
        ]
      };
    } else {
      return {
        width: '30%',
        style: 'inputWrapper',
        columns: []
      };
    }
  }

  private getBirthdateColumn(
    candidature: Candidature,
    userId: string
  ): Content {
    const date = getBirthdate(candidature, userId);

    if (date) {
      return {
        style: 'inputWrapper',
        columns: [
          [
            {
              text: 'Fecha de nacimiento'
            },
            {
              text: date,
              style: 'inputText'
            }
          ]
        ]
      };
    } else {
      return null;
    }
  }

  private getPersonalDataSectionBusiness(
    candidature: Candidature,
    report: CreateAnalysisDto,
    canvasImg: string,
    radio: number
  ): Content[] {
    return [
      {
        text: 'Información de la empresa',
        style: 'sectionTitle'
      },
      {
        columns: [
          {
            width: '40%',
            style: 'inputWrapper',
            columns: [
              [
                {
                  text: 'Representante de la empresa'
                },
                {
                  text: getBusinessRepresentativeName(
                    candidature,
                    report.userId
                  ),
                  style: 'inputText'
                }
              ]
            ]
          },
          {
            width: '40%',
            style: 'inputWrapper',
            columns: [
              [
                {
                  text: 'Correo electrónico'
                },
                {
                  text: getTenantEmail(candidature, report.userId),
                  style: 'inputText'
                }
              ]
            ]
          },
          {
            width: '20%',
            style: 'inputWrapper',
            columns: [
              [
                {
                  text: 'Teléfono'
                },
                {
                  text: getTenantPhone(candidature, report.userId),
                  style: 'inputText'
                }
              ]
            ]
          }
        ]
      },
      {
        columns: [
          {
            width: '40%',
            style: 'inputWrapper',
            columns: [
              [
                {
                  text: 'Razón social'
                },
                {
                  text: report.businessName,
                  style: 'inputText'
                }
              ]
            ]
          },
          {
            width: '40%',
            style: 'inputWrapper',
            columns: [
              [
                {
                  text: 'CIF'
                },
                {
                  text: report.cif,
                  style: 'inputText'
                }
              ]
            ]
          }
        ]
      },
      {
        image: canvasImg,
        fit: [radio * 2, radio * 2],
        absolutePosition: { x: 450, y: 155 }
      }
    ];
  }

  private getAssetDataSection(
    candidature: Candidature,
    report: CreateAnalysisDto,
    role: string
  ): Content[] {
    if (role === 'business') {
      return this.getAssetDataSectionBusiness(candidature, report);
    } else {
      return this.getAssetDataSectionTenant(candidature, report);
    }
  }

  private getAssetDataSectionTenant(
    candidature: Candidature,
    report: CreateAnalysisDto
  ): Content[] {
    let rentalPrice = report.rentalPrice;
    if (rentalPrice === null || rentalPrice === undefined) {
      rentalPrice = candidature.asset.rentalPrice;
    }
    return [
      {
        text: 'Información del activo',
        style: 'sectionTitle'
      },
      {
        columns: [
          {
            width: '40%',
            style: 'inputWrapper',
            columns: [
              [
                {
                  text: 'Referencia catastral'
                },
                {
                  text: report.cadastralReference,
                  style: 'inputText'
                }
              ]
            ]
          },
          {
            style: 'inputWrapper',
            columns: [
              [
                {
                  text: 'Dirección del activo'
                },
                {
                  text: getAssetAddress(candidature, this.translate),
                  style: 'inputText'
                }
              ]
            ]
          }
        ]
      },
      {
        columns: [
          {
            width: '40%',
            style: 'inputWrapper',
            columns: [
              [
                {
                  text: 'Alquiler en la zona'
                },
                {
                  text: `${getAssetRentalArea(candidature, report) || ''} €`,
                  style: 'inputText'
                }
              ]
            ]
          },
          {
            style: 'inputWrapper',
            columns: [
              [
                {
                  text: 'Alquiler del activo'
                },
                {
                  text: `${rentalPrice} €`,
                  style: 'inputText'
                }
              ]
            ]
          }
        ]
      }
    ];
  }

  private getAssetDataSectionBusiness(
    candidature: Candidature,
    report: CreateAnalysisDto
  ): Content[] {
    return [
      {
        text: 'Información del activo',
        style: 'sectionTitle'
      },
      {
        columns: [
          {
            style: 'inputWrapper',
            columns: [
              [
                {
                  text: 'Dirección del activo'
                },
                {
                  text: getAssetAddress(candidature, this.translate),
                  style: 'inputText'
                }
              ]
            ]
          }
        ]
      },
      {
        columns: [
          {
            width: '30%',
            style: 'inputWrapper',
            columns: [
              [
                {
                  text: 'Alquiler en la zona'
                },
                {
                  text: `${getAssetRentalArea(candidature, report)} €`,
                  style: 'inputText'
                }
              ]
            ]
          },
          {
            width: '30%',
            style: 'inputWrapper',
            columns: [
              [
                {
                  text: 'Alquiler del activo'
                },
                {
                  text: `${candidature.asset.rentalPrice} €`,
                  style: 'inputText'
                }
              ]
            ]
          },
          {
            width: '40%',
            style: 'inputWrapper',
            columns: [
              [
                {
                  text: 'Referencia catastral'
                },
                {
                  text: report.cadastralReference,
                  style: 'inputText'
                }
              ]
            ]
          }
        ]
      }
    ];
  }

  private getViabilityDataSection(
    candidature: Candidature,
    report: CreateAnalysisDto,
    catalogue: AnalysisCatalogue,
    b64: string,
    role: string
  ): Content[] {
    if (role === 'business') {
      return this.getViabilityDataSectionBusiness(
        candidature,
        report,
        catalogue,
        b64,
        role
      );
    } else {
      return this.getViabilityDataSectionTenant(
        candidature,
        report,
        catalogue,
        b64,
        role
      );
    }
  }

  private getViabilityDataSectionTenant(
    candidature: Candidature,
    report: CreateAnalysisDto,
    catalogue: AnalysisCatalogue,
    b64: string,
    role: string
  ): Content[] {
    return [
      {
        columns: [
          {
            text: 'Análisis de viabilidad',
            style: 'sectionTitle',
            width: '40%'
          },
          {
            marginTop: 20,
            text: this.getViabilityDataSectionTenantNumberText(candidature)
          }
        ]
      },
      {
        columns: [
          {
            width: '17%',
            alignment: 'center',
            columns: [
              {
                image: b64,
                height: 225,
                // width: ,
                fit: [50, 225],
                marginTop: 15
              }
            ]
          },
          [
            ...this.getSourceOfIncomeSection(
              candidature,
              report,
              catalogue,
              role
            ),
            ...this.getJobStabilitySection(
              candidature,
              report,
              catalogue,
              role
            ),
            ...this.getUnpaidSection(report, catalogue)
          ]
        ]
      }
    ];
  }

  private getViabilityDataSectionTenantNumberText(
    candidature: Candidature
  ): string {
    let text = `Esta candidatura lleva asociado${
      candidature.tenantCandidatureList?.length > 1 ? 's' : ''
    } ${
      candidature.tenantCandidatureList?.filter(
        (tenant: TenantCandidature) => !tenant.user.guarantor
      ).length
    } inquilino${candidature.tenantCandidatureList?.length > 1 ? 's' : ''}`;
    const areThereGuarantor = candidature.tenantCandidatureList?.some(
      (tenant: TenantCandidature) => tenant.user.guarantor || false
    );
    if (areThereGuarantor) {
      const numberOfGuarantor = candidature.tenantCandidatureList?.filter(
        (tenant: TenantCandidature) => tenant.user.guarantor || false
      ).length;
      text = `${text} y ${numberOfGuarantor} avalista${
        numberOfGuarantor > 1 ? 's' : ''
      }`;
    }
    return text;
  }

  private getSourceOfIncomeSection(
    candidature: Candidature,
    report: CreateAnalysisDto,
    catalogue: AnalysisCatalogue,
    role?: string
  ): Content[] {
    if (!!role && role === 'business') {
      return this.getSourceOfIncomeSectionBusiness(report);
    } else {
      return this.getSourceOfIncomeSectionTenant(
        candidature,
        report,
        catalogue,
        role
      );
    }
  }

  private getSourceOfIncomeSectionTenant(
    candidature: Candidature,
    report: CreateAnalysisDto,
    catalogue: AnalysisCatalogue,
    role: string
  ): Content[] {
    return [
      {
        marginTop: 10,
        columns: [
          {
            width: 'auto',
            text: 'Fuentes de ingresos',
            bold: true
          },
          {
            text: '\tVerificado\t',
            background: '#31ab16',
            color: 'white',
            width: 120,
            alignment: 'center'
          }
        ]
      },
      {
        marginTop: 10,
        text: 'Se analiza en detalle la aportación de documentación facilitada, filtrando a través de servicios de detección de fraude. También se realizan las verificaciones pertinentes de las nóminas facilitadas.'
      },
      this.getSourceOfIncomeSectionTenantContent(
        candidature,
        report,
        catalogue,
        role
      )
    ];
  }

  private getSourceOfIncomeSectionTenantContent(
    candidature: Candidature,
    report: CreateAnalysisDto,
    catalogue: AnalysisCatalogue,
    role: string
  ): Content {
    if (role === 'student') {
      return {
        style: 'inputWrapper',
        columns: [
          [
            {
              text: this.translate.instant(
                'components.preview_report.student_text'
              ) as string,
              style: 'tenantInputText'
            }
          ]
        ]
      };
    } else {
      return {
        columns: [
          {
            width: '25%',
            style: 'inputWrapper',
            columns: [
              [
                {
                  text: 'Cargo'
                },
                {
                  text: getPosition(
                    report.position,
                    catalogue,
                    this.translate.currentLang
                  ),
                  style: 'tenantInputText'
                }
              ]
            ]
          },
          {
            width: '25%',
            style: 'inputWrapper',
            columns: [
              [
                {
                  text: 'Ingresos netos'
                },
                {
                  text: `${report.income || '0'} €`,
                  style: 'tenantInputText'
                }
              ]
            ]
          },
          {
            width: '25%',
            style: 'inputWrapper',
            columns: [
              [
                {
                  text: 'Esfuerzo neto'
                },
                {
                  text: calculateEffort(report, candidature),
                  style: 'tenantInputText'
                }
              ]
            ]
          },
          {
            width: '25%',
            style: 'inputWrapper',
            columns: [
              [
                {
                  text: 'Esfuerzo candidatura'
                },
                {
                  text: this.jointEffort,
                  style: 'tenantInputText'
                }
              ]
            ]
          }
        ]
      };
    }
  }

  private getSourceOfIncomeSectionBusiness(
    report: CreateAnalysisDto
  ): Content[] {
    return [
      {
        marginTop: 10,
        columns: [
          {
            width: 'auto',
            text: 'Resultados económicos',
            bold: true
          },
          {
            text: '\tVerificado\t',
            background: '#31ab16',
            color: 'white',
            width: 120,
            alignment: 'center'
          }
        ]
      },
      {
        marginTop: 10,
        text: 'Se analiza en detalle la aportación de documentación facilitada, filtrando a través de servicios de detección de fraude. También verifican todos los datos económicos presentados.'
      },
      {
        columns: [
          {
            width: '50%',
            style: 'inputWrapper',
            columns: [
              [
                {
                  text: 'Ventas año anterior'
                },
                {
                  text: `${report.previousYearSales || ''} €`,
                  style: 'tenantInputText'
                }
              ]
            ]
          },
          {
            width: '50%',
            style: 'inputWrapper',
            columns: [
              [
                {
                  text: 'Ventas último trimestre'
                },
                {
                  text: `${report.lastQuarterSales || ''} €`,
                  style: 'tenantInputText'
                }
              ]
            ]
          }
        ]
      },
      {
        columns: [
          {
            width: '50%',
            style: 'inputWrapper',
            columns: [
              [
                {
                  text: 'Resultado año anterior'
                },
                {
                  text: `${report.previousYearExploit || ''} €`,
                  style: 'tenantInputText'
                }
              ]
            ]
          },
          {
            width: '50%',
            style: 'inputWrapper',
            columns: [
              [
                {
                  text: 'Resultados anteriores'
                },
                {
                  text: this.translate.instant(
                    calculatePreviousPeriodResultsValue(
                      Number(report.previousPeriodResults)
                    )
                  ) as string,
                  style: 'tenantInputText'
                }
              ]
            ]
          }
        ]
      }
    ];
  }

  private getViabilityDataSectionBusiness(
    candidature: Candidature,
    report: CreateAnalysisDto,
    catalogue: AnalysisCatalogue,
    b64: string,
    role: string
  ): Content[] {
    return [
      {
        text: 'Análisis de viabilidad',
        style: 'sectionTitle'
      },
      {
        columns: [
          {
            width: '15%',
            alignment: 'center',
            columns: [
              {
                svg: '<svg width="41px" height="289px" viewBox="0 0 41 289" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"><title>Group 17</title><g id="Empresas" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"><g id="Artboard-Copy-10" transform="translate(-50.000000, -418.000000)"><g id="Group-14" transform="translate(49.000000, 380.010000)"><g id="Group-17" transform="translate(1.000000, 37.990000)"><polygon id="Rectangle-Copy" fill="#021F37" points="19 13 21 13 21 274 19 274"></polygon><g id="Group-4-Copy" transform="translate(1.000000, 249.000000)"><circle id="Oval" fill="#021F37" cx="20" cy="20" r="20"></circle><g id="005-oferta" transform="translate(10.817019, 9.230769)" fill="#FFFFFF" fill-rule="nonzero"><g id="Group" transform="translate(2.072502, 0.000000)"><polygon id="Rectangle" transform="translate(2.019694, 10.083529) rotate(-135.000000) translate(-2.019694, -10.083529) " points="-0.121622127 9.36856677 4.16101104 9.36856677 4.16101104 10.7984922 -0.121622127 10.7984922"></polygon><polygon id="Path" points="12.1081718 3.02829577 9.07987605 0 8.07379772 1.01110872 11.0970631 4.0394045"></polygon><path d="M8.1492536,6.76587677 L9.95013381,4.31104565 L7.79712618,2.15300763 L5.34732545,3.95388784 C4.81410393,4.34625839 4.34124712,4.8191152 3.94887657,5.35233672 L2.16308753,7.78704628 L4.31609516,9.9450843 L6.75080472,8.16432565 C7.28402623,7.7719551 7.75688305,7.29909829 8.1492536,6.76587677 Z" id="Path"></path><path d="M9.05472409,7.23370319 L8.95914665,7.35946299 C8.5064114,7.97820116 7.95809871,8.52148346 7.34439093,8.97421871 L7.21863114,9.06476576 L10.3827475,12.2288821 L12.2138101,10.3927892 L9.05472409,7.23370319 Z" id="Path"></path><polygon id="Path" points="14.0096599 12.188639 12.1785973 14.0247319 14.4925775 16.3387121 16.32364 14.5026191"></polygon><polygon id="Path" points="12.9230953 11.1020744 11.0920327 12.9381673 11.4642817 13.3104163 13.2953443 11.4743234"></polygon></g><g id="Group" transform="translate(0.000000, 17.032906)"><path d="M11.0417097,2.61580366 L0.553343082,2.61580366 L0.54831269,2.61580366 C0.251519583,2.61077327 0.00503039165,2.85223207 0,3.14902517 L0,4.2758329 L11.5699008,4.2758329 L11.5699008,3.14902517 C11.5699008,3.13896439 11.5699008,3.133934 11.5699008,3.12890361 C11.5699008,2.84217128 11.328442,2.61077327 11.0417097,2.61580366 Z" id="Path"></path><path d="M9.50744022,0.528191123 C9.50744022,0.226367624 9.24585986,0 8.94403636,0 L2.6510164,0 C2.64598601,0 2.63592523,0 2.63089483,0 C2.34416251,0.00503039165 2.1077341,0.241458799 2.11276449,0.528191123 L2.11276449,1.60972533 L9.50744022,1.60972533 L9.50744022,0.528191123 Z" id="Path"></path></g></g></g><g id="Group-4" transform="translate(0.000000, 148.000000)"><circle id="Oval" fill="#021F37" cx="20" cy="20" r="20"></circle><g id="008-bolso" transform="translate(11.538414, 12.350169)" fill="#FFFFFF" fill-rule="nonzero"><path d="M10.3132427,5.53846106 L6.28445889,5.53846106 C5.91373932,5.53846106 5.61448376,5.83771662 5.61448376,6.20843619 C5.61448376,6.57915576 5.91373932,6.87841132 6.28445889,6.87841132 L10.3132427,6.87841132 C10.6839622,6.87841132 10.9832178,6.57915576 10.9832178,6.20843619 C10.9832178,5.83771662 10.6839622,5.53846106 10.3132427,5.53846106 Z" id="Path"></path><path d="M11.7112574,9.88436639 C9.48694001,10.5632745 7.11076155,10.5632745 4.89091063,9.87989989 L0.973789375,8.68734417 L0.973789375,13.7523561 C0.973789375,14.4937953 1.57676699,15.0967729 2.32267263,15.1012394 L14.2750289,15.1012394 C15.0164681,15.1012394 15.6194457,14.4982618 15.6239122,13.7523561 L15.6239122,8.68734417 L11.7112574,9.88436639 Z" id="Path"></path><path d="M15.2889246,3.25161262 L1.32664294,3.25161262 C1.31770994,3.25161262 1.31324344,3.25161262 1.30877694,3.25161262 C0.580737299,3.26054562 -0.00437431292,3.85905674 4.56139126e-05,4.58709638 L4.56139126e-05,6.44069423 C-0.00437431292,6.98114084 0.312747248,7.46798943 0.808528843,7.67791497 C0.808528843,7.68238147 0.804062342,7.68238147 0.804062342,7.68238147 C0.84426085,7.70024748 0.884459357,7.71364698 0.924657865,7.72704648 L5.15890068,9.02233173 C7.20902457,9.65210835 9.39760999,9.65210835 11.4477339,9.02233173 L15.6775102,7.72704648 L15.6819767,7.72704648 C15.7221752,7.71364698 15.7668402,7.69578098 15.8070387,7.67791497 C15.8115052,7.67791497 15.8115052,7.67344847 15.8159717,7.67344847 C16.2983538,7.45905643 16.6110089,6.97667434 16.6065424,6.44516074 L16.6154754,6.44516074 L16.6154754,4.58709638 C16.6154754,4.57816338 16.6154754,4.57369688 16.6154754,4.56923038 C16.6110089,3.84119074 16.0169643,3.25161262 15.2889246,3.25161262 Z M10.3132427,7.77171149 L6.28445889,7.77171149 C5.86907431,7.77171149 5.47155574,7.60645096 5.17676668,7.3161284 C5.17676668,7.3116619 5.17230018,7.3116619 5.17230018,7.3116619 C4.88197762,7.02133935 4.71671709,6.62382077 4.71671709,6.20843619 C4.72118359,5.34193502 5.42242423,4.64516089 6.28445889,4.64516089 L10.3132427,4.64516089 C10.7286272,4.64069439 11.1261458,4.80595492 11.4209349,5.09627748 C11.7157239,5.39553303 11.8765392,5.78858511 11.8765392,6.20396969 C11.8809845,7.07047086 11.1797438,7.77171149 10.3132427,7.77171149 Z" id="Shape"></path><path d="M10.0229201,0 L6.3246574,0 C5.96733733,0.00446650086 5.62788327,0.147394528 5.37775922,0.401985077 C5.12763517,0.656575626 4.98917364,0.996029691 4.99364015,1.34888326 L4.99364015,2.67990051 L5.88694032,2.67990051 L5.88694032,1.34441676 C5.88694032,1.22382123 5.93160532,1.11215871 6.01646884,1.0272952 C6.10133236,0.942431681 6.21299488,0.893300171 6.3291239,0.893300171 L10.0184536,0.893300171 C10.2775107,0.897766672 10.4829697,1.09875921 10.4874362,1.35334976 L10.4874362,2.67990051 L11.3807364,2.67990051 L11.3807364,1.34441676 C11.3762699,0.594044614 10.7777588,0.00446650086 10.0229201,0 Z" id="Path"></path></g></g><g id="Group-4"><circle id="Oval" fill="#021F37" cx="20.5" cy="20.5" r="20.5"></circle><g id="001-euro" transform="translate(7.884598, 12.639549)" fill="#FFFFFF" fill-rule="nonzero"><path d="M10.0871818,8.72075926 C9.70402014,6.90712743 8.37061758,5.4408955 6.60296515,4.88403389 C6.11251823,4.73587805 5.60163603,4.65924572 5.09075382,4.65924572 C3.82376595,4.65924572 2.60275747,5.13436617 1.66273421,5.98753946 C-0.416556365,7.87780362 -0.564712205,11.1014703 1.32555196,13.1807609 C3.21581612,15.2600515 6.43948284,15.4133162 8.51877342,13.523052 C9.58140841,12.5625935 10.1893582,11.1934291 10.1893582,9.75274132 C10.1893582,9.41045024 10.1535965,9.05794152 10.0871818,8.72075926 Z M7.34374435,13.1398903 C6.00012415,13.6558814 4.48280399,13.3289168 3.47125722,12.3122612 C3.12896615,11.9750789 2.85819858,11.5663732 2.68449863,11.1219056 L1.90284885,11.1219056 C1.62186364,11.1219056 1.39196665,10.8920086 1.39196665,10.6110234 C1.39196665,10.3300382 1.62186364,10.1001412 1.90284885,10.1001412 L2.4239487,10.1001412 C2.40351341,9.9111148 2.40351341,9.72719721 2.41373106,9.53817079 L1.90284885,9.53817079 C1.62186364,9.53817079 1.39196665,9.3082738 1.39196665,9.02728859 C1.39196665,8.74630337 1.62186364,8.51640638 1.90284885,8.51640638 L2.62830158,8.51640638 C2.80711036,8.01574182 3.09320439,7.56616548 3.47125722,7.19322147 C4.48791282,6.17656587 6.00523297,5.85981891 7.34374435,6.37070111 C7.60429427,6.47287755 7.73712365,6.76408041 7.63494721,7.02973916 C7.53277077,7.29028908 7.24156791,7.42311846 6.97590916,7.32094202 C6.01545061,6.94799801 4.92727151,7.18300382 4.19670996,7.90845655 C4.02301001,8.09237415 3.87485417,8.29161821 3.75224244,8.51129756 L6.7306857,8.51129756 C7.01167091,8.51129756 7.24156791,8.74119455 7.24156791,9.02217976 C7.24156791,9.30316498 7.01167091,9.53306197 6.7306857,9.53306197 L3.45082194,9.53306197 C3.43038665,9.72208839 3.43549547,9.9111148 3.4661484,10.0950324 L6.46502695,10.0950324 C6.74601217,10.0950324 6.97590916,10.3249294 6.97590916,10.6059146 C6.97590916,10.8868998 6.74601217,11.1167968 6.46502695,11.1167968 L3.83909241,11.1167968 C3.93105121,11.2904968 4.05366294,11.4488702 4.20181878,11.5816996 C4.92727151,12.3122612 6.01545061,12.547267 6.98101798,12.1794318 C6.98101798,12.1794318 6.98101798,12.1794318 6.98101798,12.1845406 C7.24667673,12.087473 7.53787959,12.2151936 7.64005603,12.4808523 C7.73712365,12.746511 7.6094031,13.0377139 7.34374435,13.1398903 Z" id="Shape"></path><path d="M16.3199447,0.49555574 C15.277745,0.178808772 13.8472748,0 12.3861517,0 C10.914811,0 9.53542901,0.178808772 8.50344696,0.49555574 C7.4254855,0.827629174 7.19047969,1.17502907 7.18537087,1.24655258 C7.19047969,1.25166141 7.19047969,1.25166141 7.19047969,1.26187905 C7.2160238,1.37427314 7.52255312,1.72678186 8.62094986,2.04352883 C9.63760546,2.33984051 10.9761168,2.50332281 12.3861517,2.50332281 C13.8472748,2.50332281 15.277745,2.31940522 16.3148359,2.00776707 C17.4285591,1.67569364 17.6686737,1.32318491 17.6686737,1.25166141 C17.6686737,1.1801379 17.4285591,0.832737996 16.3199447,0.49555574 Z" id="Path"></path><path d="M12.1613636,12.1079083 L12.1613636,12.0159495 C11.6913519,12.0108407 11.2315579,11.9852966 10.7973081,11.9495348 C10.5623022,12.5574846 10.2302288,13.1245639 9.81641423,13.6303373 C10.6031728,13.7580578 11.3950402,13.8295813 12.1869077,13.839799 L12.1613636,12.1079083 Z" id="Path"></path><path d="M16.6060388,2.98355209 C15.4616626,3.33095199 13.9596689,3.5199784 12.3810429,3.5199784 C10.8739404,3.5199784 9.4383614,3.34627845 8.32974701,3.02442266 C7.94658535,2.92224622 7.57875016,2.7791992 7.22624144,2.60039043 L7.2620032,4.04107825 C7.83930009,4.2607576 8.37572641,4.5621781 8.8610645,4.94533976 C8.87639097,4.95044858 8.88660861,4.95044858 8.90193508,4.9555574 C9.88282891,5.20588968 11.1191639,5.33871906 12.3810429,5.33871906 C13.842166,5.33871906 15.277745,5.15480146 16.3148359,4.84316332 C17.4285591,4.50598106 17.6686737,4.15347234 17.6686737,4.08705765 C17.6635649,4.00020768 17.689109,3.9133577 17.7350884,3.84183419 L17.7350884,2.53397574 C17.3774709,2.72300216 16.999418,2.87626682 16.6060388,2.98355209 Z" id="Path"></path><path d="M16.6111476,5.82405715 C15.4616626,6.17145705 13.9596689,6.36048347 12.3810429,6.36048347 C11.6045019,6.36048347 10.8330698,6.30939525 10.0667465,6.21232763 C10.4805611,6.7845157 10.7870904,7.42822728 10.9812257,8.11280944 C11.4205844,8.14346237 11.8957048,8.16389766 12.3861517,8.16389766 C12.4014782,8.16389766 12.4168047,8.16389766 12.4321311,8.16389766 C13.2699779,7.23920086 15.5382949,6.77940688 17.7350884,6.71810101 L17.7350884,5.36426317 C17.3774709,5.55328958 17.0045269,5.70144542 16.6111476,5.82405715 Z" id="Path"></path><path d="M11.0834021,10.9430969 C11.4256932,10.968641 11.7782019,10.9839674 12.1409283,10.9890763 L12.1102753,9.22142382 C12.1102753,9.21120618 12.1051665,9.19587971 12.1051665,9.18566207 C11.7884195,9.18055325 11.4767814,9.1703356 11.1753609,9.14990032 C11.1957962,9.3542532 11.2060138,9.55349726 11.200905,9.75274132 C11.200905,10.1512294 11.1600344,10.5497176 11.0834021,10.9430969 Z" id="Path"></path><path d="M23.605125,11.5868084 L23.605125,10.27895 C23.2475074,10.4679764 22.8694546,10.6161322 22.4760753,10.7285263 C21.336808,11.0657086 19.8552496,11.2496262 18.3021677,11.2496262 C16.8308269,11.2496262 15.4156832,11.0759262 14.3172865,10.7693969 C13.9136895,10.6621116 13.525419,10.5139558 13.152475,10.3249294 L13.1729103,11.4948496 L13.183128,11.9342083 C13.6020514,12.4246553 15.5025332,13.0530404 18.3072765,13.0530404 C19.7888349,13.0530404 21.1784345,12.8793404 22.225743,12.5625935 C23.3292486,12.2254112 23.5642544,11.8780113 23.5642544,11.8064878 C23.5642544,11.7298555 23.6102338,11.6532231 23.6102338,11.5868084 L23.605125,11.5868084 L23.605125,11.5868084 Z" id="Path"></path><path d="M22.5220547,13.5383785 C21.3776786,13.8908872 19.8859025,14.0748048 18.3072765,14.0748048 C16.4476653,14.0748048 14.4194629,13.7938196 13.2035633,13.1756521 L13.2291074,14.6521017 C13.2291074,14.6572105 13.2291074,14.6572105 13.2291074,14.6623193 C13.2291074,14.7389517 13.4487867,15.0812427 14.531857,15.4133162 C15.5536214,15.7249543 16.9330034,15.8986543 18.4094529,15.8986543 C19.8807937,15.8986543 21.2601756,15.7198455 22.2972665,15.3979897 C23.3803368,15.0608074 23.6102338,14.7082987 23.6102338,14.6367752 L23.6102338,13.0888021 C23.2628339,13.2727197 22.9001075,13.4259844 22.5220547,13.5383785 Z" id="Path"></path><path d="M22.2461783,8.23542117 C21.2039786,7.91356538 19.7735084,7.75008307 18.3123853,7.75008307 L18.2459706,7.75008307 C16.8819151,7.75008307 15.5842743,7.90334773 14.588054,8.18433295 C13.5407455,8.48064463 13.1678015,8.81271806 13.1167133,8.95576508 C13.1269309,8.98130919 13.1320397,9.01196212 13.1371486,9.04261505 C13.2342162,9.19587971 13.6275955,9.51262668 14.6033805,9.78339425 C15.6149273,10.0643795 16.9330034,10.2227529 18.3123853,10.2227529 C19.7735084,10.2227529 21.1528904,10.0541618 22.2001989,9.74252368 C23.3343574,9.40534142 23.5897985,9.0528327 23.5949073,8.97620037 C23.5795809,8.88935039 23.3241398,8.56238578 22.2461783,8.23542117 Z" id="Path"></path></g></g></g></g></g></g></svg>',
                marginTop: 15,
                height: 260
              }
            ]
          },
          [
            ...this.getSourceOfIncomeSection(
              candidature,
              report,
              catalogue,
              role
            ),
            ...this.getJobStabilitySection(
              candidature,
              report,
              catalogue,
              role
            ),
            ...this.getUnpaidSection(report, catalogue, role)
          ]
        ]
      }
    ];
  }

  private getJobStabilitySection(
    candidature: Candidature,
    report: CreateAnalysisDto,
    catalogue: AnalysisCatalogue,
    role: string
  ): Content[] {
    if (!!role && role === 'business') {
      return this.getJobStabilitySectionBusiness(
        candidature,
        report,
        catalogue
      );
    } else {
      return this.getJobStabilitySectionTenant(report, catalogue, role);
    }
  }

  private getJobStabilitySectionTenant(
    report: CreateAnalysisDto,
    catalogue: AnalysisCatalogue,
    role: string
  ): Content[] {
    return [
      {
        marginTop: 10,
        columns: [
          {
            width: 'auto',
            text: 'Estabilidad laboral',
            bold: true
          },
          {
            text: '\tVerificado\t',
            background: '#31ab16',
            color: 'white',
            width: 120,
            alignment: 'center'
          },
          this.getWarningLabel(report)
        ]
      },
      {
        marginTop: 10,
        text: 'Se analizan los diferentes indicadores de estabilidad laboral (tipo de contrato, duración y antigüedad) junto con el sector de actividad al que se dedica la empresa.'
      },
      this.getJobStabilitySectionTenantContent(report, catalogue, role)
    ];
  }

  private getWarningLabel(report: CreateAnalysisDto): Column {
    if (report.riskWarning) {
      return {
        text: '\tAviso de RIESGO\t',
        background: '#ff8803',
        color: 'white',
        width: 90,
        alignment: 'center',
        marginLeft: 2
      };
    } else {
      return null;
    }
  }

  private getJobStabilitySectionTenantContent(
    report: CreateAnalysisDto,
    catalogue: AnalysisCatalogue,
    role: string
  ): Content {
    if (role === 'student') {
      return {
        style: 'inputWrapper',
        columns: [
          [
            {
              text: this.translate.instant(
                'components.preview_report.without_work'
              ) as string,
              style: 'tenantInputText'
            }
          ]
        ]
      };
    } else {
      return {
        columns: [
          {
            width: '25%',
            style: 'inputWrapper',
            columns: [
              [
                {
                  text: 'Razón social'
                },
                {
                  text: report.businessName,
                  style: 'tenantInputText'
                }
              ]
            ]
          },
          {
            width: '25%',
            style: 'inputWrapper',
            columns: [
              [
                {
                  text: 'Sector de actividad'
                },
                {
                  text: getBusinessActivitySector(
                    report.sector,
                    catalogue,
                    this.translate.currentLang
                  ),
                  style: 'tenantInputText'
                }
              ]
            ]
          },
          {
            width: '25%',
            style: 'inputWrapper',
            columns: [
              [
                {
                  text: 'Tipo de contrato'
                },
                {
                  text: getContractType(
                    report.contractType,
                    catalogue,
                    this.translate.currentLang
                  ),
                  style: 'tenantInputText'
                }
              ]
            ]
          },
          {
            width: '25%',
            style: 'inputWrapper',
            columns: [
              [
                {
                  text: 'Antigüedad'
                },
                {
                  text: getAntiquity(
                    report.antiquity,
                    catalogue,
                    this.translate.currentLang
                  ),
                  style: 'tenantInputText'
                }
              ]
            ]
          }
        ]
      };
    }
  }

  private getJobStabilitySectionBusiness(
    candidature: Candidature,
    report: CreateAnalysisDto,
    catalogue: AnalysisCatalogue
  ): Content[] {
    return [
      {
        marginTop: 10,
        columns: [
          {
            width: 'auto',
            text: 'Estabilidad empresarial',
            bold: true
          },
          {
            text: '\tVerificado\t',
            background: '#31ab16',
            color: 'white',
            width: 120,
            alignment: 'center'
          }
        ]
      },
      {
        marginTop: 10,
        text: 'Se analiza en detalle la documentación facilitada por el solicitante desde el equipo de investigación y análisis. A continuación algunos indicadores clave:'
      },
      {
        columns: [
          {
            width: '25%',
            style: 'inputWrapper',
            columns: [
              [
                {
                  text: 'Antigüedad'
                },
                {
                  text: getAntiquity(
                    report.businessAntiquity,
                    catalogue,
                    this.translate.currentLang,
                    true
                  ),
                  style: 'tenantInputText'
                }
              ]
            ]
          },
          {
            width: '25%',
            style: 'inputWrapper',
            columns: [
              [
                {
                  text: 'Sector de actividad'
                },
                {
                  text: getBusinessActivitySector(
                    report.businessActivitySector,
                    catalogue,
                    this.translate.currentLang
                  ),
                  style: 'tenantInputText'
                }
              ]
            ]
          },
          {
            width: '25%',
            style: 'inputWrapper',
            columns: [
              [
                {
                  text: 'Capacidad de pago'
                },
                {
                  text: calculateBusinessPaymentCapacity(
                    candidature,
                    report,
                    catalogue,
                    this.translate.currentLang
                  ),
                  style: 'tenantInputText'
                }
              ]
            ]
          },
          {
            width: '25%',
            style: 'inputWrapper',
            columns: [
              [
                {
                  text: 'Riesgo'
                },
                {
                  text: calculateBusinesssRisk(
                    report,
                    catalogue,
                    this.translate.currentLang
                  ),
                  style: 'tenantInputText'
                }
              ]
            ]
          }
        ]
      }
    ];
  }

  private getUnpaidSection(
    report: CreateAnalysisDto,
    catalogue: AnalysisCatalogue,
    role?: string
  ): Content[] {
    if (role === 'business') {
      return this.getUnpaidSectionBusiness(report, catalogue);
    } else {
      return this.getUnpaidSectionTenant(report, catalogue);
    }
  }

  private getUnpaidSectionTenant(
    report: CreateAnalysisDto,
    catalogue: AnalysisCatalogue
  ): Content[] {
    return [
      {
        marginTop: 10,
        columns: [
          {
            width: 'auto',
            text: 'Impagos o incidencias',
            bold: true
          },
          {
            text: '\tConsultado\t',
            background: '#31ab16',
            color: 'white',
            width: 120,
            alignment: 'center'
          }
        ]
      },
      {
        marginTop: 10,
        text: 'Se analizan las incidencias notificadas en fuentes de acceso público. Además se consultan las diferentes incidencias en los bureaus negativos de impagos.'
      },
      {
        columns: [
          {
            width: '50%',
            style: 'inputWrapper',
            columns: [
              [
                {
                  text: 'Incidencias verificadas'
                },
                {
                  text: getJudicialIncidents(
                    report.judicialIncidents,
                    catalogue,
                    this.translate.currentLang
                  ),
                  style: 'tenantInputText'
                }
              ]
            ]
          },
          {
            width: '50%',
            style: 'inputWrapper',
            columns: [
              [
                {
                  text: this.translate.instant(
                    getTraceabilityValue(report.traceability)
                  ) as string,
                  style: 'tenantInputText'
                }
              ]
            ]
          }
        ]
      },
      {
        columns: [
          {
            width: '50%',
            style: 'inputWrapper',
            columns: [
              [
                {
                  text: 'Impagos'
                },
                {
                  text: getNonPayments(
                    report.nonPaymentsConfirmed,
                    catalogue,
                    this.translate.currentLang
                  ),
                  style: 'tenantInputText'
                }
              ]
            ]
          },
          this.getInternationalBlacklistContent(report, 50)
        ]
      }
    ];
  }

  private getUnpaidSectionBusiness(
    report: CreateAnalysisDto,
    catalogue: AnalysisCatalogue
  ): Content[] {
    return [
      {
        marginTop: 10,
        columns: [
          {
            width: 'auto',
            text: 'Impagos o incidencias',
            bold: true
          },
          {
            text: '\tConsultado\t',
            background: '#31ab16',
            color: 'white',
            width: 120,
            alignment: 'center'
          }
        ]
      },
      {
        marginTop: 10,
        text: 'Se analizan las incidencias notificadas en fuentes de acceso público. Además se consultan las diferentes incidencias en los bureaus negativos de impagos.'
      },
      {
        columns: [
          {
            width: '30%',
            style: 'inputWrapper',
            columns: [
              [
                {
                  text: 'Incidencias judiciales'
                },
                {
                  text: getJudicialIncidents(
                    report.businessJudicialIncidents,
                    catalogue,
                    this.translate.currentLang,
                    true
                  ),
                  style: 'tenantInputText'
                }
              ]
            ]
          },
          {
            width: '30%',
            style: 'inputWrapper',
            columns: [
              [
                {
                  text: 'Impagos'
                },
                {
                  text: getNonPayments(
                    report.businessNoPaymentsConfirmed,
                    catalogue,
                    this.translate.currentLang,
                    true
                  ),
                  style: 'tenantInputText'
                }
              ]
            ]
          },
          this.getInternationalBlacklistContent(report, 40)
        ]
      }
    ];
  }

  private getInternationalBlacklistContent(
    report: CreateAnalysisDto,
    width: number
  ): Column {
    if (report.aml) {
      return null;
    } else {
      return {
        width: `${width}%`,
        style: 'inputWrapper',
        columns: [
          [
            {
              text: 'Sanciones internacionales'
            },
            {
              text: this.translate.instant(
                getInternationalBlacklist(report)
              ) as string,
              style: 'tenantInputText'
            }
          ]
        ]
      };
    }
  }

  private getConditioningSection(report: CreateAnalysisDto): Content[] {
    if (report.conditioning) {
      return [
        {
          columns: [
            {
              width: 'auto',
              text: 'Condicionantes',
              style: 'sectionTitle'
            },
            this.getWarningLabelConditioning(report)
          ]
        },
        {
          text: `${format(
            new Date(report.conditioning.creationDate),
            'dd/MM/yyyy'
          )} - ${report.conditioning.description}`
        }
      ];
    } else {
      return [];
    }
  }

  private getWarningLabelConditioning(report: CreateAnalysisDto): Column {
    if (report.riskWarning) {
      return {
        text: '\tAviso de RIESGO\t',
        background: '#ff8803',
        color: 'white',
        width: 110,
        alignment: 'center',
        marginLeft: 20,
        marginTop: 23
      };
    } else {
      return null;
    }
  }

  private getFooter(
    reports: CreateAnalysisDto[],
    role: string,
    candidature: Candidature
  ): Content[] {
    const date = reports[0].updatedDate
      ? reports[0].updatedDate
      : reports[0].creationDate;
    const stringDate = format(new Date(date), 'dd/MM/yyyy');
    if (role === 'business') {
      return this.getFooterBusiness(stringDate, reports[0], candidature);
    } else {
      return this.getFooterTenant(stringDate);
    }
  }

  private getFooterTenant(date: string): Content[] {
    return [
      {
        margin: [20, 0],
        columns: [
          [
            {
              text: `Este informe fue generado el día ${date}`
            },
            {
              text: 'La información de este análisis de viabilidad no fundamenta ninguna decisión del contratante basada únicamente en un tratamiento automatizado. Su objeto es facilitar la adopción de decisiones en el marco de la actividad del contratante, por lo que EBI queda liberada de cualquier responsabilidad por aquellos eventuales resultados y/o consecuencias derivadas de su utilización y/o interpretación.',
              fontSize: 8
            }
          ]
        ]
      }
    ];
  }

  private getFooterBusiness(
    date: string,
    report: CreateAnalysisDto,
    candidature: Candidature
  ): Content[] {
    return [
      {
        margin: [20, 0],
        columns: [
          [
            {
              text: `Ref. Informe: ${report.reportId} | Ref. Candidatura: ${candidature.id} | Verificado el día ${date}`,
              fontSize: 9
            },
            {
              text: 'La información de este análisis de viabilidad no fundamenta ninguna decisión del contratante basada únicamente en un tratamiento automatizado. Su objeto es facilitar la adopción de decisiones en el marco de la actividad del contratante, por lo que EBI queda liberada de cualquier responsabilidad por aquellos eventuales resultados y/o consecuencias derivadas de su utilización y/o interpretación.',
              fontSize: 8,
              marginTop: 10
            }
          ]
        ]
      }
    ];
  }

  private getAmlTable(
    report: CreateAnalysisDto,
    candidature: Candidature
  ): Content {
    return [
      {
        text: `Resultado Análisis Blanqueo de Capitales`,
        style: 'sectionTitle'
      },
      {
        marginTop: 10,
        text: 'Se analizan las listas internacionales de sanciones, prevención de blanqueo de capitales,  PEP (personas politicamente expuestas) así como las listas de Terrorismo Internacional y noticias adversas con especial relevancia'
      },
      {
        marginTop: 10,
        columns: [
          {
            text: 'Inquilino:',
            width: 50
          },
          {
            text: getTenantName(candidature, report.userId),
            color: '#2ba5d8'
          }
        ]
      },
      {
        table: {
          widths: [300, 100],
          body: [
            this.getIsPublicPerson(report.aml.isPublicPerson),
            this.getWasPublicPerson(report.aml.wasPublicPerson),
            this.getIsSanctionated(report.aml.isSanctionated),
            this.getWasSanctionated(report.aml.wasSanctionated),
            this.getIsTerrorist(report.aml.isTerrorist),
            this.getHasMedia(report.aml.hasMedia),
            this.getHasAdverseInfo(report.aml.hasAdverseInfo),
            this.getIsHighRisk(report.aml.isHighRisk)
          ]
        }
      }
    ];
  }

  private getIsPublicPerson(isPublicPerson: boolean): TableCell[] {
    const value = this.getAmlValue(isPublicPerson);
    return ['Políticamente expuesto (PEP)', value];
  }

  private getWasPublicPerson(wasPublicPerson: boolean): TableCell[] {
    const value = this.getAmlValue(wasPublicPerson);
    return ['Políticamente expuesto en el pasado (PEP archivado)', value];
  }

  private getIsSanctionated(isSanctionated: boolean): TableCell[] {
    const value = this.getAmlValue(isSanctionated);
    return ['Presente en listas internacionales', value];
  }

  private getWasSanctionated(wasSanctionated: boolean): TableCell[] {
    const value = this.getAmlValue(wasSanctionated);
    return ['Listas Internacionales archivadas', value];
  }

  private getIsTerrorist(isTerrorist: boolean): TableCell[] {
    const value = this.getAmlValue(isTerrorist);
    return ['Presente en listas de Terrorismo', value];
  }

  private getHasMedia(hasMedia: boolean): TableCell[] {
    const value = this.getAmlValue(hasMedia);
    return ['Aparece en los medios de comunicación', value];
  }

  private getHasAdverseInfo(hasAdverseInfo: boolean): TableCell[] {
    const value = this.getAmlValue(hasAdverseInfo);
    return ['Tiene publicidad adversa en medios', value];
  }

  private getIsHighRisk(isHighRisk: boolean): TableCell[] {
    const value = this.getAmlValue(isHighRisk);
    return ['Personas de alto riesgo', value];
  }

  private getAmlValue(value: boolean): TableCell {
    const text = value ? '\t Sí \t' : '\tNo\t';
    const background = value ? this.dangerColor : '#31ab16';
    return {
      text,
      background,
      color: 'white',
      alignment: 'center',
      width: 100
    };
  }
}
